/**
 * Catálogo de permissões — FONTE DA VERDADE.
 *
 * Chave: `modulo.pagina.acao`.
 *
 * ── Invariantes (não quebre nenhuma sem ler o porquê) ──
 *
 * 1. O MÓDULO É DERIVADO, nunca armazenado como concessão própria. Ele é o primeiro segmento
 *    da chave. Isso torna estruturalmente IMPOSSÍVEL o estado "módulo desligado + página
 *    ligada": conceder qualquer `clientes.*` já implica acesso ao módulo `clientes`. Foi um
 *    requisito explícito, e resolvê-lo por construção é melhor do que por validação.
 *
 * 2. A PÁGINA é a linha com `action: 'view'`. Toda página tem exatamente uma; as demais ações
 *    daquela página pendem dela. Por isso `kind` é derivável e existe só para o SQL ler rápido.
 *
 * 3. SÓ AS 28 ROTAS DA SIDEBAR (+ /whatsapp-saude, deep link vivo e gateado por papel).
 *    O `App.tsx` declara ~110 `path=`, mas semear 110 deixaria 92 rotas sem chave — e aí
 *    fail-closed mata 92 telas, fail-open faz a feature não significar nada. Sub-rotas herdam
 *    a chave do pai (ex.: /deals/kanban usa `clientes.deals.view`).
 *
 * 4. `active: false` = a rota está DESLIGADA no produto (a escada da Sidebar já a esconde de
 *    todo mundo hoje). Não é permissão negada, é feature que não existe. Ver §Zonas mortas.
 *
 * 5. "Grupos e permissões" NÃO É uma chave deste catálogo. A tela que edita a matriz é gateada
 *    por PAPEL, não por permissão — se fosse uma linha da própria matriz, um clique trancaria
 *    o último admin para fora. Mesma decisão do Fluxo de Caixa (packages/shared/src/pages.ts).
 *
 * ── Dois eixos ortogonais ──
 * Este catálogo responde "esta PESSOA pode?". Ele NÃO responde "esta ORG contratou?" — isso
 * continua em `pages`/`organization_pages` e nas flags de org (`goal_metrics_enabled`,
 * `commissions_enabled`). A Sidebar exige os dois. Não fundir os eixos: a tentativa anterior de
 * mover gate de org para dentro de outro lugar quebrou em silêncio (Sidebar.tsx:186-190).
 */

export type ModuleKey =
  | 'inicio' | 'clientes' | 'operacoes' | 'comunicacao' | 'materiais' | 'automacao' | 'desempenho'
  | 'objetivos' | 'financeiro' | 'configuracoes';

export interface PermissionModule {
  key: ModuleKey;
  label: string;
  sortOrder: number;
}

export interface PermissionPage {
  module: ModuleKey;
  page: string;
  label: string;
  route: string;
  /** false = rota desligada no produto hoje (hard-off da escada da Sidebar). */
  active?: boolean;
  /** Ações além de `view`, na ordem em que aparecem na matriz. */
  actions?: { action: string; label: string }[];
}

export const PERMISSION_MODULES: readonly PermissionModule[] = [
  { key: 'inicio',        label: 'Início',        sortOrder: 1 },
  { key: 'clientes',      label: 'Clientes',      sortOrder: 2 },
  // Na posição da Sidebar: depois de Vendas, a leitura é "vendi, agora entrego".
  { key: 'operacoes',     label: 'Operações',     sortOrder: 3 },
  { key: 'comunicacao',   label: 'Comunicação',   sortOrder: 4 },
  // Documentos que o time estuda e a IA usa como régua (docs/specs/materiais-documentacao-playbook.md).
  { key: 'materiais',     label: 'Materiais',     sortOrder: 5 },
  { key: 'automacao',     label: 'Automação',     sortOrder: 6 },
  { key: 'desempenho',    label: 'Desempenho',    sortOrder: 7 },
  // OKR da companhia e das áreas (docs/specs/okr-modulo-objetivos.md). Eixo de org: `okr_enabled`.
  { key: 'objetivos',     label: 'Objetivos',     sortOrder: 8 },
  { key: 'financeiro',    label: 'Financeiro',    sortOrder: 9 },
  { key: 'configuracoes', label: 'Configurações', sortOrder: 10 },
] as const;

export const PERMISSION_PAGES: readonly PermissionPage[] = [
  // ── Início ────────────────────────────────────────────────────────────────
  { module: 'inicio', page: 'dashboard',        label: 'Dashboard',            route: '/dashboard' },
  { module: 'inicio', page: 'relatorio_squad',  label: 'Dashboard (Squad)',    route: '/relatorio-squad' },
  { module: 'inicio', page: 'activities',       label: 'Atividades',           route: '/activities',
    actions: [ { action: 'create', label: 'Criar atividade' },
               { action: 'edit',   label: 'Editar atividade' },
               { action: 'delete', label: 'Excluir atividade' },
               { action: 'feedback_vendedor', label: 'Gravar feedback para o vendedor' } ] },
  { module: 'inicio', page: 'activities_focus', label: 'Execução focada',      route: '/activities/focus' },
  { module: 'inicio', page: 'vendedor',         label: 'Painel do Vendedor',   route: '/vendedor' },

  // ── Clientes ──────────────────────────────────────────────────────────────
  { module: 'clientes', page: 'leads',     label: 'Leads',     route: '/leads',
    actions: [ { action: 'create', label: 'Criar lead' },
               { action: 'edit',   label: 'Editar lead' },
               { action: 'delete', label: 'Excluir lead' },
               { action: 'import', label: 'Importar leads' },
               { action: 'export', label: 'Exportar leads' } ] },
  { module: 'clientes', page: 'companies', label: 'Empresas',  route: '/companies',
    actions: [ { action: 'create', label: 'Criar empresa' },
               { action: 'edit',   label: 'Editar empresa' },
               { action: 'delete', label: 'Excluir empresa' },
               { action: 'export', label: 'Exportar empresas' } ] },
  { module: 'clientes', page: 'deals',     label: 'Negócios',  route: '/deals',
    actions: [ { action: 'create',   label: 'Criar negócio' },
               { action: 'edit',     label: 'Editar negócio' },
               { action: 'delete',   label: 'Excluir negócio' },
               { action: 'transfer', label: 'Transferir dono do negócio' },
               { action: 'export',   label: 'Exportar negócios' },
               // Painéis da ficha do negócio (docs/specs/contrato-paineis-editaveis.md §11). Vale
               // para a org inteira. Sem concessão padrão (20261003120000).
               { action: 'configurar_campos', label: 'Escolher os campos e os painéis da ficha do negócio (vale para a org)' } ] },
  // A página ZapSign. A CHAVE segue `clientes.contratos.*` (901 grupos a têm; renomear
  // revogaria todos), só a rota e o rótulo mudaram: /contratos virou /documentos na Onda 1 do
  // módulo de Projetos, e o contrato COMERCIAL (a Carteira) vive em `operacoes.carteira`.
  { module: 'clientes', page: 'contratos', label: 'Documentos (assinatura)', route: '/documentos',
    actions: [ { action: 'create', label: 'Criar documento' },
               { action: 'edit',   label: 'Editar documento' },
               { action: 'delete', label: 'Excluir documento' } ] },
  // Catálogo de produtos, tabelas de preço e categorias. Em Vendas desde 09/10/2026: a chave foi
  // RENOMEADA de `operacoes.produtos.*` (migration 20261009150000; as concessões acompanham pelo
  // ON UPDATE CASCADE). A antiga `clientes.products.view` (zona morta, inativa) não é reaproveitada.
  // Concessão inicial (decisão do usuário): ver e editar para Administradores, Acesso total e Gestor
  // nas orgs que usam o catálogo. `edit` soma-se a admin/owner, que editam sem a chave — RLS e telas
  // usam `catalog_pode_editar(org)`.
  { module: 'clientes', page: 'produtos', label: 'Produtos', route: '/produtos',
    actions: [ { action: 'edit', label: 'Editar o catálogo (produtos, preços, tabelas, unidades e categorias)' } ] },

  // ── Operações ─────────────────────────────────────────────────────────────
  // A estrutura Scrum do Ninho sobre `work_items` + Carteira (docs/specs/modulo-operacoes-scrum.md).
  // O eixo de ORG é `organizations.projects_enabled` (Sidebar, por prefixo /operacoes/); estas
  // chaves são o eixo da PESSOA. ⚠️ Declarar a chave faz a Sidebar exigi-la no ato — a concessão
  // inicial vai na migration 20260929001100, antes do seed gerado.
  { module: 'operacoes', page: 'dashboard', label: 'Dashboard de operações', route: '/operacoes/dashboard' },
  { module: 'operacoes', page: 'quadro',    label: 'Quadro',                 route: '/operacoes/quadro' },
  // Projeto = `work_items` type `portfolio`, o nível entre Contrato e Entrega
  // (docs/specs/operacoes-nivel-projeto.md). Concessão inicial na migration 20261008120500:
  // quem vê Entregas vê Projetos.
  { module: 'operacoes', page: 'projetos',  label: 'Projetos',               route: '/operacoes/projetos',
    actions: [ { action: 'create', label: 'Criar projeto' },
               { action: 'edit',   label: 'Editar projeto' },
               { action: 'delete', label: 'Excluir projeto' } ] },
  { module: 'operacoes', page: 'entregas',  label: 'Entregas',               route: '/operacoes/entregas',
    actions: [ { action: 'create', label: 'Criar entrega' },
               { action: 'edit',   label: 'Editar entrega' },
               { action: 'delete', label: 'Excluir entrega' } ] },
  { module: 'operacoes', page: 'tarefas',   label: 'Tarefas',                route: '/operacoes/tarefas',
    actions: [ { action: 'create', label: 'Criar tarefa' },
               { action: 'edit',   label: 'Editar tarefa' },
               { action: 'delete', label: 'Excluir tarefa' } ] },
  { module: 'operacoes', page: 'sprints',   label: 'Sprints',                route: '/operacoes/sprints',
    actions: [ { action: 'manage', label: 'Criar, iniciar e encerrar sprints' } ] },
  // Chave própria, e não `clientes.contratos.view`: reusar a chave antiga daria a Carteira aos
  // 901 grupos que têm a página ZapSign, por acidente de história.
  { module: 'operacoes', page: 'carteira',  label: 'Carteira de contratos',  route: '/operacoes/contratos',
    actions: [ { action: 'create', label: 'Criar contrato' },
               { action: 'edit',   label: 'Editar contrato' },
               // Aba Comunicação. Também é a chave de VISIBILIDADE das conversas e grupos
               // restritos no Atendimento (RLS, 20260923120000) — sem concessão padrão.
               { action: 'comunicacao', label: 'Comunicação do contrato (ver conversas e grupos restritos)' },
               // Cobranças da Kamino na aba Financeiro (pago, atrasado, a vencer). Também é a
               // chave de leitura de `client_contract_receivables` (RLS). Sem concessão padrão.
               // Spec: docs/specs/contrato-saude-por-ia.md (Fase 1).
               { action: 'financeiro', label: 'Ver cobranças do cliente na Kamino (pago, atrasado, a vencer)' },
               // Aba Resultados: meta e realizado da org do cliente (rpc_contract_results*).
               // A mesma chave vê e configura o vínculo e os indicadores. Sem concessão padrão.
               // Spec: docs/specs/contrato-aba-resultados.md.
               { action: 'resultados', label: 'Ver resultados do cliente (metas e realizado da org do cliente)' },
               // "+ Cadastrar pessoa" no seletor do time (CS, Supervisor, Vendedor alocado).
               // Sem concessão padrão (20260929155721): grupo a grupo.
               { action: 'cadastrar_colaborador', label: 'Cadastrar pessoa sem acesso ao CRM (time do contrato)' },
               // Mesmo atalho, com "Precisa acessar o CRM?". Só oferece grupos marcados como
               // liberados e sem permissão de gestão (20260929174726). Sem concessão padrão.
               { action: 'convidar_usuario', label: 'Convidar usuário com acesso ao CRM (time do contrato)' },
               // Ciclo de vida (docs/specs/contratos-ciclo-de-vida-e-cancelamento.md). Sem concessão
               // padrão: vai só para o grupo "Gestor de Operação" (20260929172532).
               { action: 'aditivos', label: 'Estender, pausar, renegociar e expandir contrato' },
               // Painéis Empresa/Contrato da ficha (docs/specs/contrato-paineis-editaveis.md).
               // Escolha vale para a org inteira. Sem concessão padrão (20261002180001).
               { action: 'configurar_campos', label: 'Escolher os campos e os painéis da ficha do contrato (vale para a org)' } ] },
  // Esteira de cancelamento. A `view` vai para quem já vê a Carteira; `manage` só para o grupo
  // "Gestor de Operação". Solicitar cancelamento usa `operacoes.carteira.edit`.
  { module: 'operacoes', page: 'cancelamento', label: 'Cancelamento', route: '/operacoes/cancelamento',
    actions: [ { action: 'manage', label: 'Gerenciar a esteira de cancelamento (mover, reverter, encerrar, reativar, configurar)' } ] },
  // Rateio e entrega de dossiês da Atticus (docs/specs/atticus-rateio-entrega.md). Eixo da ORG:
  // `atticus_rateio_enabled`, além de `projects_enabled`. Sem concessão padrão: a tela Rateio é
  // só da Diretoria (RN-10), e o grupo é escolhido à mão.
  { module: 'operacoes', page: 'rateio', label: 'Rateio de dossiês', route: '/operacoes/rateio',
    actions: [ { action: 'edit', label: 'Definir limites, prioridades e ordem do rateio' } ] },
  { module: 'operacoes', page: 'pedidos', label: 'Gestão de pedidos', route: '/operacoes/pedidos',
    actions: [ { action: 'edit', label: 'Criar e editar OS e itens' } ] },
  // CS do rateio (spec §12): não tem tela própria — os casos vivem na pipeline "CS clientes ativos"
  // (Negócios). A rota é só de referência; as chaves dizem quem vê os casos e quem os trabalha
  // (assumir, mover e liberar a pasta pela etapa "Pasta liberada").
  { module: 'operacoes', page: 'cs', label: 'CS do rateio (casos)', route: '/operacoes/cs',
    actions: [ { action: 'edit', label: 'Assumir e mover casos de CS, inclusive liberar a pasta' } ] },
  // Relatórios › Operações › Teses (burn-up por tese + mídia). Fica no FIM do módulo para não mudar
  // a primeira rota de ninguém. Sem concessão padrão: grupos escolhidos depois do deploy do front.
  // Spec: docs/specs/relatorio-operacoes-teses.md.
  { module: 'operacoes', page: 'teses', label: 'Relatório de teses (burn-up e mídia)', route: '/relatorio-operacoes/teses' },

  // ── Comunicação ───────────────────────────────────────────────────────────
  { module: 'comunicacao', page: 'email',            label: 'E-mail',            route: '/email',
    actions: [ { action: 'send', label: 'Enviar e-mail' } ] },
  { module: 'comunicacao', page: 'chat',             label: 'Atendimento',       route: '/chat',
    actions: [ { action: 'send', label: 'Enviar mensagem' } ] },
  { module: 'comunicacao', page: 'whatsapp_oficial', label: 'WhatsApp Oficial',  route: '/whatsapp-oficial' },
  // Fora do MENU desde que virou seção de Configurações, mas a ROTA segue viva como deep link
  // e é gateada por papel na escada (Sidebar.tsx). Sem chave própria ficaria órfã.
  { module: 'comunicacao', page: 'whatsapp_saude',   label: 'Saúde do WhatsApp', route: '/whatsapp-saude' },
  { module: 'comunicacao', page: 'sms',              label: 'SMS',               route: '/sms' },
  { module: 'comunicacao', page: 'discadora',        label: 'Discadora',         route: '/discadora' },
  { module: 'comunicacao', page: 'vox',              label: 'Vox AI',            route: '/vox', active: false },

  // ── Materiais ───────────────────────────────────────────────────────────────────────────
  // `view` lê os playbooks PUBLICADOS da org. Rascunho e escrita exigem `editar`; publicar (o que
  // o time e a IA leem) exige `publicar`. Não confundir com os playbooks de Operações (Onda 2 do
  // módulo de projetos, `work_item_templates`), que são modelos de tarefa.
  { module: 'materiais', page: 'playbooks', label: 'Playbooks', route: '/materiais/playbooks',
    actions: [ { action: 'editar',   label: 'Criar e editar playbooks' },
               { action: 'publicar', label: 'Publicar playbooks' } ] },
  // Quadro de pastas do jurídico com os dados do cliente mascarados no banco, para a Atticus
  // demonstrar o processo de entrega (docs/specs/pastas-demonstracao.md). Gate de org à parte:
  // `pastas_demo_fontes`. Era `desempenho.*` até 07/10/2026; a chave foi renomeada no banco
  // (ON UPDATE CASCADE leva as concessões), migration 20261007230000.
  { module: 'materiais', page: 'pastas_demonstracao', label: 'Pastas (demonstração)', route: '/pastas-demonstracao' },

  // ── Automação ─────────────────────────────────────────────────────────────
  { module: 'automacao', page: 'automations', label: 'Automações',    route: '/automations' },
  { module: 'automacao', page: 'sequences',   label: 'Cadências',     route: '/sequences' },
  { module: 'automacao', page: 'follow_ups',  label: 'Follow-ups IA', route: '/follow-ups' },

  // ── Desempenho ────────────────────────────────────────────────────────────
  { module: 'desempenho', page: 'marketing', label: 'Marketing', route: '/marketing', actions: [] },
  { module: 'desempenho', page: 'metas',             label: 'Métricas e Metas',          route: '/metas',
    // `configurar_relatorios` grava o padrão da ORG dos indicadores da Visão geral dos relatórios
    // (tabela `report_metric_selection`, linha com user_id nulo). A escolha pessoal não exige chave.
    actions: [ { action: 'configurar_relatorios', label: 'Definir indicadores padrão dos relatórios' } ] },
  { module: 'desempenho', page: 'metricas',          label: 'Relatórios personalizados', route: '/metricas' },
  { module: 'desempenho', page: 'relatorio_midhaus', label: 'Relatório Midhaus',         route: '/relatorio-midhaus' },
  { module: 'desempenho', page: 'relatorio_atticus', label: 'Relatório Atticus',         route: '/relatorio-atticus' },
  { module: 'desempenho', page: 'gestao_agenda',     label: 'Gestão de Agenda',          route: '/gestao-agenda',
    // `manage` substitui a coluna `memberships.can_manage_agenda`, que era um eixo paralelo por
    // pessoa. É lida por 13 RPCs de agenda MAIS `can_manage_booking`, que aparece em ~12 policies
    // de booking — os dois portões passam a ler esta chave. Ver 20260831*_agenda_permissao_catalogo.
    actions: [ { action: 'manage', label: 'Gerenciar a agenda de todos' } ] },
  { module: 'desempenho', page: 'agendamento',       label: 'Agendamento',               route: '/agendamento' },
  { module: 'desempenho', page: 'lead_scoring',      label: 'Lead Scoring',              route: '/lead-scoring', active: false },
  // Hoje é a aba Configurações › Controle de pool; a rota /gestor redireciona para lá.
  { module: 'desempenho', page: 'gestor',            label: 'Controle de pool',          route: '/gestor' },

  // ── Objetivos (OKR) ───────────────────────────────────────────────────────
  // Toda a org lê (`view`); escrita por nível. Check-in não tem chave: é do dono do
  // resultado-chave (RPC/policy no banco). Sem concessão na migration (20261008210100): os
  // grupos recebem pela matriz depois do deploy. Eixo de org: `organizations.okr_enabled`.
  { module: 'objetivos', page: 'okr',    label: 'Objetivos (OKR)',     route: '/objetivos',
    actions: [ { action: 'companhia_edit', label: 'Criar e editar objetivos da companhia' },
               { action: 'area_edit',      label: 'Criar e editar objetivos de área' } ] },
  { module: 'objetivos', page: 'ciclos', label: 'Ciclos e propósito', route: '/objetivos/ciclos',
    actions: [ { action: 'edit', label: 'Criar, ativar e fechar ciclos' } ] },

  // ── Financeiro ────────────────────────────────────────────────────────────
  // `comissoes_pagas` É a antiga `desempenho.comissionamento`, re-parenteada. A chave MUDOU
  // (`financeiro.comissoes_pagas.*`), e como o módulo é derivado do 1º segmento não havia como
  // mover sem renomear. As 918 concessões vivas são preservadas pelo `ON UPDATE CASCADE` de
  // access_group_permissions.permission_key -> permissions.key, num UPDATE atômico feito na
  // migration 20260921100400 — que precisa rodar ANTES do seed gerado, senão o seed desativaria
  // a chave antiga e as concessões apontariam para linha inativa.
  { module: 'financeiro', page: 'comissoes_pagas',     label: 'Comissões pagas',     route: '/financeiro/comissoes-pagas',
    // `supervisao_unidade` é a chave da tela CROSS-ORG da Supervisão, e é deliberadamente
    // separada de `view`. Reusar `view` não discriminaria ninguém: ela herdou as concessões de
    // `desempenho.comissionamento.view` na migração do módulo e hoje está em 104–147 orgs por
    // admin. Esta nasce concedida a NINGUÉM — conceder é um ato sobre uma pessoa, que é o ponto.
    actions: [ { action: 'editar_regras', label: 'Editar regras e vínculos' },
               { action: 'ajustar', label: 'Gerenciar ajustes' },
               { action: 'fechar', label: 'Fechar apuração' },
               { action: 'reabrir', label: 'Reabrir apuração' },
               { action: 'registrar_pagamento', label: 'Registrar pagamento' },
               { action: 'gerenciar_senha', label: 'Gerenciar segunda senha' },
               { action: 'supervisao_unidade', label: 'Ver comissões da unidade (cross-org)' } ] },
  { module: 'financeiro', page: 'comissoes_recebidas', label: 'Comissões recebidas', route: '/financeiro/comissoes-recebidas',
    actions: [ { action: 'vincular', label: 'Vincular organização ao contrato' },
               { action: 'mapear_metrica', label: 'Mapear métrica do contrato' },
               // Cadastro da regra de success fee na aba Comissões do contrato (rpc_contract_commission_*).
               // Sem concessão padrão. Spec: docs/specs/comissao-success-fee-no-crm.md.
               { action: 'editar_regras', label: 'Criar e editar a regra de success fee do contrato' } ] },

  // ── Configurações ─────────────────────────────────────────────────────────
  // `settings.view` abre a tela. As abas destrutivas são ações separadas — é o que permite um
  // grupo "Gestor" ver Configurações sem poder mexer em usuários.
  { module: 'configuracoes', page: 'settings', label: 'Configurações', route: '/settings',
    actions: [ { action: 'manage_users',        label: 'Gerenciar usuários' },
               { action: 'manage_teams',        label: 'Gerenciar equipes' },
               { action: 'manage_integrations', label: 'Gerenciar integrações' },
               { action: 'manage_org',          label: 'Editar dados da organização' } ] },
] as const;

/** Uma linha por chave — o formato que o seed SQL e a matriz consomem. */
export interface PermissionEntry {
  key: string;
  moduleKey: ModuleKey;
  pageKey: string;
  actionKey: string;
  kind: 'page' | 'action';
  label: string;
  route: string | null;
  active: boolean;
  sortOrder: number;
}

function build(): PermissionEntry[] {
  const out: PermissionEntry[] = [];
  let order = 0;
  for (const m of PERMISSION_MODULES) {
    for (const p of PERMISSION_PAGES.filter((x) => x.module === m.key)) {
      const active = p.active !== false;
      out.push({
        key: `${p.module}.${p.page}.view`,
        moduleKey: p.module, pageKey: p.page, actionKey: 'view', kind: 'page',
        label: p.label, route: p.route, active, sortOrder: (order += 10),
      });
      for (const a of p.actions ?? []) {
        out.push({
          key: `${p.module}.${p.page}.${a.action}`,
          moduleKey: p.module, pageKey: p.page, actionKey: a.action, kind: 'action',
          label: a.label, route: null, active, sortOrder: (order += 10),
        });
      }
    }
  }
  return out;
}

export const PERMISSIONS: readonly PermissionEntry[] = build();
export const PERMISSION_KEYS: readonly string[] = PERMISSIONS.map((p) => p.key);

/**
 * As chaves que ABREM TELA — as únicas que a Sidebar consulta, via `routeToPermission`.
 * Uma pessoa sem nenhuma delas entra no CRM e vê o menu vazio, sem "acesso negado".
 */
export const PAGE_PERMISSION_KEYS: readonly string[] = PERMISSIONS
  .filter((p) => p.kind === 'page' && p.active)
  .map((p) => p.key);

/** `false` = a pessoa não consegue abrir NENHUMA tela com esse conjunto de chaves. */
export function abreAlgumaTela(chaves: Iterable<string>): boolean {
  const tem = chaves instanceof Set ? chaves : new Set(chaves);
  return PAGE_PERMISSION_KEYS.some((k) => tem.has(k));
}

/** Rota → chave de página. Sub-rotas resolvem pelo prefixo mais longo (ver routeToPermission). */
export const ROUTE_TO_PERMISSION: Readonly<Record<string, string>> = Object.fromEntries(
  PERMISSIONS.filter((p) => p.route).map((p) => [p.route as string, p.key]),
);

/**
 * Resolve a chave de uma rota qualquer do router pelo PREFIXO MAIS LONGO.
 * `/deals/kanban` → `clientes.deals.view`; `/activities/focus` casa com a própria entrada, não
 * com `/activities` — daí o "mais longo" em vez do primeiro que casar.
 * Devolve null quando a rota não tem chave: o chamador decide (a Sidebar esconde, o guard nega).
 */
export function routeToPermission(pathname: string): string | null {
  let melhor: { rota: string; key: string } | null = null;
  for (const [rota, key] of Object.entries(ROUTE_TO_PERMISSION)) {
    if (pathname === rota || pathname.startsWith(rota + '/')) {
      if (!melhor || rota.length > melhor.rota.length) melhor = { rota, key };
    }
  }
  return melhor?.key ?? null;
}

/**
 * A primeira tela que o conjunto de chaves abre, na ordem do menu (módulo, depois página).
 *
 * As portas de entrada do app (`/` manda para o Kanban de negócios, e trocar de org manda para
 * `/dashboard`) não têm guarda de permissão: um grupo sem Início e sem Clientes, como o
 * "Operação" da Midhaus, cairia numa tela que não deveria ver. Quem não tem a tela de entrada
 * vai para esta. `null` = não abre tela nenhuma; o chamador mantém o comportamento de antes.
 */
export function primeiraRotaPermitida(
  tem: (key: string) => boolean,
  /** Filtro da org (ex.: páginas fora de linha pelo preset). Sem ele, toda rota vale. */
  rotaVale: (route: string) => boolean = () => true,
): string | null {
  const entrada = PERMISSIONS.find((p) => p.kind === 'page' && p.active && p.route && tem(p.key) && rotaVale(p.route));
  return entrada?.route ?? null;
}
